import { FormControlLabel } from "@mui/material";

import MyDialog from "~/mui/MyDialog";
import { checkForErrorsState } from '~/utils/utils';
import { useTrans } from '~/hooks/useTrans';
import { IUserAdmin } from "~/providers/interfaces";
import { apiUpdateAdminUser } from "~/providers/api";
import { myUseState } from "~/hooks/myUseState";
import { useLang } from "~/hooks/useLang";
import Wysiwyg, { contentToHTML, htmlToContent, IWysiwygContent } from "~/components/Wysiwyg";
import { MAX_USER_NOTE_LEN } from "~/const";
import { MySwitch } from "~/mui/MySwitch";
import TextFieldNumber from "~/mui/TextFieldNumber";

import "./style.less";

interface IState {
	dialogLoading: boolean;
	errorMsg: string;
	wasChanged: boolean;
}

interface ICheckData {
	maxProjectCount: number;
	projectCountDescription: string;
}

interface IStateFormData {
	banned: boolean;
	maxProjectCount: number;
	projectCountDescription: null | IWysiwygContent;
}

// chyby formulare
type TStateErrors = {[key in keyof Pick<IStateFormData, "projectCountDescription" | "maxProjectCount">]: string; };

interface IAdminUserDataModal {
	userData: IUserAdmin;
	onChange?: (isChanged: boolean) => void;
	onSave?: (data: IUserAdmin) => void;
	onClose?: () => void;
}

export default function AdminUserDataModal({
	userData,
	onChange = () => {},
	onSave = () => {},
	onClose = () => {},
}: IAdminUserDataModal) {
	const { trans } = useTrans();
	const { lang } = useLang();
	const { state, updateState } = myUseState<IState>({
		dialogLoading: false,
		errorMsg: "",
		wasChanged: false,
	});
	const { state: stateFormData, updateState: updateFormData } = myUseState<IStateFormData>({
		banned: userData.banned,
		maxProjectCount: userData.maxProjectCount,
		projectCountDescription: userData.projectCountDescription
			? htmlToContent(userData.projectCountDescription)
			: null,
	});
	const { state: stateError, setState: setStateError } = myUseState<TStateErrors>({
		maxProjectCount: "",
		projectCountDescription: "",
	});

	function getWasChange(newState: Partial<IStateFormData>): boolean {
		const newFormDataState = {
			...stateFormData,
			...newState,
		};
		const hasNote = newFormDataState.projectCountDescription && newFormDataState.projectCountDescription.getPlainText() !== "";

		return hasNote
			|| newFormDataState.banned !== userData.banned
			|| newFormDataState.maxProjectCount !== userData.maxProjectCount;
	}

	function updateFormDataWithChange(formDataState: Partial<IStateFormData>, stateData?: Partial<IState>) {
		const wasChanged = getWasChange(formDataState);

		updateFormData(formDataState);
		updateState({
			wasChanged,
			...stateData,
		});
		onChange(wasChanged);
	}

	async function dialogOnSave() {
		const formData: ICheckData = {
			maxProjectCount: stateFormData.maxProjectCount,
			projectCountDescription: stateFormData.projectCountDescription?.getPlainText() || "",
		};
		const errorData = checkForErrorsState<ICheckData, TStateErrors>(formData, {
			maxProjectCount: { check: "number-gt0", msg: trans("adminUserDataModal.maxProjectCountError") },
			projectCountDescription: { check: "string-user-note", msg: trans("adminUserDataModal.noteLenError", { from: MAX_USER_NOTE_LEN[0], to: MAX_USER_NOTE_LEN[1] }) },
		});

		setStateError(errorData.errorsState);

		// jsou tam chyby
		if (errorData.errorsCount > 0) {
			return;
		}

		updateState({
			dialogLoading: true,
			errorMsg: "",
		});

		const updateData = await apiUpdateAdminUser(lang, {
			banned: stateFormData.banned,
			email: userData.email,
			maxProjectCount: stateFormData.maxProjectCount,
			projectCountDescription: contentToHTML(stateFormData.projectCountDescription),
		});

		if (updateData.noErrors) {
			onSave(updateData.data);
		} else {
			updateState({
				dialogLoading: false,
				errorMsg: updateData.errors.join(", "),
			});
		}
	}

	return <MyDialog title={trans("adminUserDataModal.title")} onClose={onClose} onSave={dialogOnSave} className="adminUserDataModal" dialogLoading={state.dialogLoading}
		errorMsg={state.errorMsg} saveCaption={trans("saveChanges")} confirmClose={state.wasChanged}>
		<div className="adminUserDataModal__topControls">
			<FormControlLabel
				control={<MySwitch sx={{ m: 1 }} checked={stateFormData.banned} onChange={event => updateFormDataWithChange({ banned: event.target.checked })} />}
				label={trans("adminUserDataModal.banned")}
				sx={{ marginBottom: "var(--margin-large)" }}
			/>
			<TextFieldNumber
				label={trans("adminUserDataModal.maxProjectCount")}
				value={stateFormData.maxProjectCount}
				onChange={maxProjectCount => updateFormDataWithChange({ maxProjectCount })}
				error={stateError.maxProjectCount !== ""}
				helperText={stateError.maxProjectCount}
			/>
		</div>
		<div>
			<Wysiwyg title={trans("adminUserDataModal.projectCountDescription")} content={stateFormData.projectCountDescription} error={stateError.projectCountDescription}
				onChange={content => updateFormDataWithChange({ projectCountDescription: content })} />
		</div>
	</MyDialog>;
}
